/**
 * Authenticated training-center plan detail (`/planos/:id`). Shows the plan, an
 * "outstanding payment" block for the open monthly charges — payable in-app via
 * a dynamic Asaas Pix QR (pix_online) or the static Pix key (manual) — a
 * read-only charge history, and the completed lessons.
 *
 * The outstanding block comes straight from `outstanding-payments` (Pix already
 * embedded) and is polled until it empties out. Compliance: every payer-facing
 * Pix block carries the Asaas seal + processed-by clause.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { AsaasSeal, Body, Button, ButtonVariant, Caption, H4 } from '@meiuca/ui-components'
import { AlertCircle, ArrowLeft, Check, Copy, Loader2 } from 'lucide-react'
import { NotFoundError } from '@meiuca/service'
import { formatCurrency, formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { PixPayment } from '@/components/PixPayment'
import { useAuth } from '@/contexts/AuthContext'
import { OVERDUE_PIX_NOTICE, isOverdue } from '@/lib/overdue'
import { toastManager } from '@/lib/toastManager'
import { formatPixKeyType, formatTimeRange } from '@/lib/trainingCenterLabels'
import {
  trainingCenterPlanService,
  type CompletedLessonItem,
  type MonthlyChargeItem,
  type OutstandingPaymentItem,
  type PlanDetailItem
} from '@/services/trainingCenterPlanService'
import type { PaginationInfo } from '@/services/pagination'

const POLL_INTERVAL_MS = 4000

const MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
]

function formatYearMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-')
  const idx = Number(month) - 1
  return MONTHS_PT[idx] ? `${MONTHS_PT[idx]} de ${year}` : yearMonth
}

const CHARGE_BADGES: Record<string, { text: string; color: string }> = {
  paid: { text: 'Pago', color: 'bg-feedback-success/20 text-feedback-success' },
  pending: { text: 'Pendente', color: 'bg-feedback-warning/20 text-feedback-warning' },
  failed: { text: 'Falhou', color: 'bg-feedback-error/20 text-feedback-error' }
}

const STATUS_DOT: Record<string, string> = {
  paid: 'bg-feedback-success',
  pending: 'bg-feedback-warning',
  failed: 'bg-feedback-error'
}

/** Copy with a fallback for non-secure contexts (e.g. accessing via LAN IP),
 *  where `navigator.clipboard` is unavailable. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error' }
  | { kind: 'ok'; plan: PlanDetailItem }

export function PlanDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  const [outstanding, setOutstanding] = useState<OutstandingPaymentItem[]>([])

  const [charges, setCharges] = useState<MonthlyChargeItem[]>([])
  const [chargesPage, setChargesPage] = useState<PaginationInfo | null>(null)
  const [chargesLoadingMore, setChargesLoadingMore] = useState(false)

  const [lessons, setLessons] = useState<CompletedLessonItem[]>([])
  const [lessonsPage, setLessonsPage] = useState<PaginationInfo | null>(null)
  const [lessonsLoadingMore, setLessonsLoadingMore] = useState(false)

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // id → overdue, of the outstanding items on screen (polling set-diff).
  const outstandingRef = useRef<Map<string, boolean>>(new Map())

  useEffect(() => {
    document.title = 'Plano · Meiuca'
    analyticsService.trackScreenView(SCREEN_NAMES.PLAN_DETAIL)
  }, [])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Cleanup polling on unmount.
  useEffect(() => stopPolling, [stopPolling])

  const refreshCharges = useCallback(async () => {
    if (!id) return
    try {
      const res = await trainingCenterPlanService.listMonthlyCharges(id, { limit: 12 })
      setCharges(res.data)
      setChargesPage(res.pagination)
    } catch {
      // keep the current history on a transient failure
    }
  }, [id])

  const startPolling = useCallback(() => {
    if (!id) return
    stopPolling()
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await trainingCenterPlanService.listOutstandingPayments(id)
        const freshIds = new Set(fresh.map((o) => o.id))
        // Ids that left the list were paid; remember if any was overdue.
        const paidOverdue = Array.from(outstandingRef.current).filter(([oldId]) => !freshIds.has(oldId))
        const paidSomething = paidOverdue.length > 0

        setOutstanding(fresh)
        outstandingRef.current = toOverdueMap(fresh)

        if (paidSomething) {
          analyticsService.track(ANALYTICS_EVENTS.PLAN_PAYMENT_COMPLETED, {
            plan_id: id,
            is_overdue: paidOverdue.some(([, overdue]) => overdue)
          })
          toastManager.success('Pagamento confirmado!')
          await refreshCharges()
        }
        if (fresh.length === 0) {
          stopPolling()
        }
      } catch {
        // Transient error while polling — keep trying on the next tick.
      }
    }, POLL_INTERVAL_MS)
  }, [id, refreshCharges, stopPolling])

  const loadAll = useCallback(async () => {
    if (!id) {
      setState({ kind: 'not-found' })
      return
    }
    try {
      const plan = await trainingCenterPlanService.getPlanDetail(id)
      setState({ kind: 'ok', plan })

      // Open charges come embedded in the detail — one request, softer load. The
      // dedicated `outstanding-payments` endpoint is used only for polling below.
      const embedded = plan.outstandingPayments ?? []
      setOutstanding(embedded)
      outstandingRef.current = toOverdueMap(embedded)
      if (embedded.length > 0) {
        startPolling()
      } else {
        stopPolling()
      }

      // Best-effort: the history/lessons shouldn't block the plan from rendering.
      const [chargesRes, lessonsRes] = await Promise.allSettled([
        trainingCenterPlanService.listMonthlyCharges(id, { limit: 12 }),
        trainingCenterPlanService.listCompletedLessons(id, { limit: 20 })
      ])
      if (chargesRes.status === 'fulfilled') {
        setCharges(chargesRes.value.data)
        setChargesPage(chargesRes.value.pagination)
      }
      if (lessonsRes.status === 'fulfilled') {
        setLessons(lessonsRes.value.data)
        setLessonsPage(lessonsRes.value.pagination)
      }
    } catch (err) {
      setState({ kind: err instanceof NotFoundError ? 'not-found' : 'error' })
    }
  }, [id, startPolling, stopPolling])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    loadAll()
  }, [isAuthenticated, loadAll, navigate])

  const loadMoreCharges = async () => {
    if (!id || !chargesPage?.hasMore || chargesLoadingMore) return
    setChargesLoadingMore(true)
    try {
      const res = await trainingCenterPlanService.listMonthlyCharges(id, {
        limit: 12,
        cursor: chargesPage.cursor ?? undefined
      })
      setCharges((prev) => [...prev, ...res.data])
      setChargesPage(res.pagination)
    } catch {
      toastManager.error('Erro ao carregar mais mensalidades.')
    } finally {
      setChargesLoadingMore(false)
    }
  }

  const loadMoreLessons = async () => {
    if (!id || !lessonsPage?.hasMore || lessonsLoadingMore) return
    setLessonsLoadingMore(true)
    try {
      const res = await trainingCenterPlanService.listCompletedLessons(id, {
        limit: 20,
        cursor: lessonsPage.cursor ?? undefined
      })
      setLessons((prev) => [...prev, ...res.data])
      setLessonsPage(res.pagination)
    } catch {
      toastManager.error('Erro ao carregar mais aulas.')
    } finally {
      setLessonsLoadingMore(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate('/planos')}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Meus planos
        </button>

        {state.kind === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando plano...</Body>
          </div>
        )}

        {state.kind === 'not-found' && (
          <EmptyCard
            title="Plano não encontrado"
            message="O link pode estar incorreto ou o plano não está mais disponível."
          />
        )}

        {state.kind === 'error' && (
          <EmptyCard
            title="Algo deu errado"
            message="Não conseguimos carregar este plano agora."
            onRetry={loadAll}
          />
        )}

        {state.kind === 'ok' && (
          <div className="space-y-4">
            <PlanHeader plan={state.plan} />

            {outstanding.length > 0 && (
              <OutstandingPaymentSection>
                {outstanding.map((item) => (
                  <OutstandingPaymentRow key={item.id} item={item} />
                ))}
              </OutstandingPaymentSection>
            )}

            <Section title="Mensalidades">
              {charges.length === 0 ? (
                <Caption className="text-text-secondary">Nenhuma mensalidade encontrada.</Caption>
              ) : (
                <div className="space-y-3">
                  {charges.map((charge) => (
                    <MonthlyChargeHistoryRow key={charge.id} charge={charge} />
                  ))}
                  {chargesPage?.hasMore && (
                    <LoadMore loading={chargesLoadingMore} onClick={loadMoreCharges} />
                  )}
                </div>
              )}
            </Section>

            <Section title="Aulas concluídas">
              {lessons.length === 0 ? (
                <Caption className="text-text-secondary">Nenhuma aula concluída ainda.</Caption>
              ) : (
                <div className="space-y-3">
                  {lessons.map((lesson) => (
                    <CompletedLessonRow key={lesson.id} lesson={lesson} />
                  ))}
                  {lessonsPage?.hasMore && (
                    <LoadMore loading={lessonsLoadingMore} onClick={loadMoreLessons} />
                  )}
                </div>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  )
}

function PlanHeader({ plan }: { plan: PlanDetailItem }) {
  const isActive = plan.status === 'active'
  return (
    <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <H4 className="text-text-primary">{plan.trainingCenter.name}</H4>
          {/* Plano de dependente sempre identifica o aluno (responsável agindo). */}
          {plan.isDependent && plan.studentCustomer && (
            <Caption className="text-text-secondary">Aluno: {plan.studentCustomer.name}</Caption>
          )}
        </div>
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            isActive
              ? 'bg-feedback-success/20 text-feedback-success'
              : 'bg-text-tertiary/20 text-text-tertiary'
          }`}
        >
          {isActive ? 'Ativo' : 'Inativo'}
        </span>
      </div>
      <div className="flex items-end justify-between pt-3 border-t border-border-light">
        <div>
          <Body className="text-2xl font-bold text-brand-primary m-0">
            {formatCurrency(plan.amountMonthlyCents)}
          </Body>
          <Caption className="text-text-secondary">por mês</Caption>
        </div>
        <Caption className="text-text-secondary">
          Desde {formatDateForUI(parseAPIDate(plan.startAt), 'medium')}
        </Caption>
      </div>
    </div>
  )
}

/** Highlighted card grouping the open charges the user still has to pay. */
function OutstandingPaymentSection({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-background-white shadow-sm border border-feedback-warning/40 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border-light bg-feedback-warning/10">
        <AlertCircle className="w-4 h-4 text-feedback-warning flex-shrink-0" />
        <h2 className="text-sm font-semibold text-text-primary m-0">Pagamento em aberto</h2>
      </div>
      <div className="p-5 space-y-6">{children}</div>
    </div>
  )
}

function toOverdueMap(items: OutstandingPaymentItem[]): Map<string, boolean> {
  return new Map(items.map((o) => [o.id, isOverdue(o.dueAt)]))
}

function OutstandingPaymentRow({ item }: { item: OutstandingPaymentItem }) {
  const overdue = isOverdue(item.dueAt)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Body className="text-sm font-medium text-text-primary m-0">
            {formatYearMonth(item.yearMonth)}
          </Body>
          <Caption className="text-text-secondary">{formatCurrency(item.amountCents)}</Caption>
        </div>
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            overdue ? 'bg-feedback-error/20 text-feedback-error' : 'bg-feedback-warning/20 text-feedback-warning'
          }`}
        >
          {overdue ? 'Vencida' : 'Pendente'}
        </span>
      </div>

      {item.paymentType === 'pix_online' ? (
        item.pix ? (
          <div className="space-y-4 pt-1">
            {overdue && (
              <Caption className="block text-feedback-error text-center">{OVERDUE_PIX_NOTICE(item.dueAt)}</Caption>
            )}
            <PixPayment payload={item.pix.payload} qrCodeBase64={item.pix.encodedImage} />
            <Caption className="text-text-tertiary text-center">
              Assim que o pagamento for confirmado, esta tela é atualizada automaticamente.
            </Caption>
            <div className="flex flex-col items-center gap-1 pt-3 border-t border-border-light">
              <AsaasSeal size="sm" />
              <Caption className="text-text-tertiary">Pagamento processado por Asaas IP</Caption>
            </div>
          </div>
        ) : (
          <Caption className="text-text-secondary text-center py-2">
            {overdue
              ? 'Este Pix venceu. Fale com o seu centro de treinamento para receber um novo.'
              : 'Estamos preparando o seu Pix. Atualize a página em instantes.'}
          </Caption>
        )
      ) : item.manual ? (
        <ManualPixKey
          chargeId={item.id}
          pixKey={item.manual.pixKey}
          pixKeyType={item.manual.pixKeyType}
          overdue={overdue}
        />
      ) : null}
    </div>
  )
}

function ManualPixKey({
  chargeId,
  pixKey,
  pixKeyType,
  overdue
}: {
  chargeId: string
  pixKey: string
  pixKeyType: string
  overdue: boolean
}) {
  const [copied, setCopied] = useState(false)
  const typeLabel = formatPixKeyType(pixKeyType)

  const handleCopy = async () => {
    const ok = await copyText(pixKey)
    if (ok) {
      analyticsService.track(ANALYTICS_EVENTS.PLAN_PIX_KEY_COPIED, { charge_id: chargeId, is_overdue: overdue })
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } else {
      toastManager.error('Não foi possível copiar a chave.')
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <Caption className="text-text-tertiary">
          Chave Pix {typeLabel ? `(${typeLabel})` : ''} para pagamento
        </Caption>
        <Body className="text-sm text-text-primary break-all">{pixKey}</Body>
      </div>
      <Button variant={ButtonVariant.SECONDARY} onClick={handleCopy} className="w-full justify-center">
        {copied ? (
          <>
            <Check size={16} className="mr-2" />
            Chave copiada!
          </>
        ) : (
          <>
            <Copy size={16} className="mr-2" />
            Copiar chave Pix
          </>
        )}
      </Button>
      <div className="flex flex-col items-center gap-1">
        <AsaasSeal size="sm" />
        <Caption className="text-text-tertiary">Pagamento processado por Asaas IP</Caption>
      </div>
    </div>
  )
}

/** Read-only history row: month, amount and a semantic status badge. */
function MonthlyChargeHistoryRow({ charge }: { charge: MonthlyChargeItem }) {
  const badge = CHARGE_BADGES[charge.status] ?? CHARGE_BADGES.pending

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border-light p-3">
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[charge.status] ?? STATUS_DOT.pending}`}
        />
        <div className="min-w-0">
          <Body className="text-sm font-medium text-text-primary m-0">
            {formatYearMonth(charge.yearMonth)}
          </Body>
          <Caption className="text-text-secondary">{formatCurrency(charge.amountCents)}</Caption>
        </div>
      </div>
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>{badge.text}</span>
    </div>
  )
}

function CompletedLessonRow({ lesson }: { lesson: CompletedLessonItem }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border-light p-3">
      <div className="min-w-0">
        <Body className="text-sm font-medium text-text-primary">
          {formatDateForUI(parseAPIDate(lesson.occurredAt), 'long')}
        </Body>
        <Caption className="text-text-secondary">
          {formatTimeRange(lesson.startTimeMin, lesson.endTimeMin)} · {lesson.locationName}
        </Caption>
        {lesson.trainingClassDescription && (
          <Caption className="text-text-tertiary">{lesson.trainingClassDescription}</Caption>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light">
      <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
        {title}
      </h2>
      {children}
    </div>
  )
}

function LoadMore({ loading, onClick }: { loading: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full py-2 text-sm font-medium text-brand-primary hover:bg-brand-primary/5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          Carregando...
        </>
      ) : (
        'Carregar mais'
      )}
    </button>
  )
}

function EmptyCard({
  title,
  message,
  onRetry
}: {
  title: string
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm border border-border-light">
      <H4 className="mb-2 text-text-primary">{title}</H4>
      <Body className="text-text-secondary">{message}</Body>
      {onRetry && (
        <div className="mt-4 flex justify-center">
          <Button variant={ButtonVariant.PRIMARY} onClick={onRetry}>
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  )
}
